import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { BeneficiaryInfo } from "../../interfaces/DTO/mislaka_xml_insurence_models";
import { SimulationChangeType, SimulationMimshakData } from "../../interfaces/DTO/simulation";
import { useContacts, usePartialUpdateContact } from "../../hooks/crm/useContacts";
import { simulationService } from "../../services/SimulationService";
import { useSnackbarStore, SnackbarType } from "../../stores/useSnackbarStore";
import { formatHandlerCode, formatName } from "../../utils/namesFormater";
import { formatProductTypeUniversal } from "../../utils/productTypeUniversalFormatter";
import {
  beneficiaryName,
  definitionProblem,
  familyChildren,
  matchesDefinition,
  suggestBeneficiariesFromFamily,
  withBeneficiaries,
} from "../../utils/beneficiariesUtil";
import BeneficiariesDefinitionEditor from "../CRM/contacts/beneficiaries/BeneficiariesDefinitionEditor";
import SimulationLinkSendStep from "../UserTask/QuickActions/SimulationLinkSendStep";
import PreparedFormsStep from "../UserTask/FormsPreparation/PreparedForms/PreparedFormsStep";
import { PreparedFormItem, usePreparedForms } from "../UserTask/FormsPreparation/PreparedForms/usePreparedForms";
import { taskFormService } from "../../services/forms/taskFormService";

/** a fund in the beneficiaries table - its beneficiaries now, and the MimshakData row it came from */
export interface BeneficiaryFund {
  key: string;
  contact_id: number;
  policy_number: string;
  handler_code: string;
  product_type?: number | null;
  adjusted_product_type?: number | null;
  beneficiaries: BeneficiaryInfo[];
  row: MimshakData;
}

const rightAlignedText = { primary: { style: { textAlign: "right" as const } }, secondary: { style: { textAlign: "right" as const } } };

interface Props {
  open: boolean;
  onClose: () => void;
  funds: BeneficiaryFund[];
}

/**
 * עדכון מוטבים: הגדרת המוטבים (מהגדרת הלקוח / הצעה לפי המשפחה), בחירת הקופות להחיל עליהן (מסומנות מראש אלה
 * שלא תואמות) ויצירת משימת עדכון מוטבים לכל קופה – בסימולציה אחת, עם לינק ללקוח.
 * המשימה לא נבדקת בדוחות היומיים – רק בפרודוקציה של החודש הבא.
 */
const BeneficiaryUpdateDialog: React.FC<Props> = ({ open, onClose, funds }) => {
  const { data: allContacts = [] } = useContacts();
  const updateContact = usePartialUpdateContact();
  const showSnackbar = useSnackbarStore((s) => s.showSnackbar);

  const contactIds = useMemo(() => Array.from(new Set(funds.map((f) => f.contact_id))), [funds]);
  const [contactId, setContactId] = useState<number | null>(null);
  const [definition, setDefinition] = useState<BeneficiaryInfo[]>([]);
  const [saveAsDefinition, setSaveAsDefinition] = useState(true);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [title, setTitle] = useState("עדכון מוטבים");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdSimulationId, setCreatedSimulationId] = useState<number | null>(null);
  // after the funds: preparing the forms (each fund - now in the system, or later in an external one)
  const [step, setStep] = useState<"details" | "forms">("details");
  const preparedForms = usePreparedForms();

  const contact = allContacts.find((c) => c.id === contactId);
  // in a family - the other member is the spouse
  const spouse: Contact | undefined = useMemo(() => {
    const otherId = contactIds.find((id) => id !== contactId);
    return otherId ? allContacts.find((c) => c.id === otherId) : undefined;
  }, [contactIds, contactId, allContacts]);
  const familySuggestion = useMemo(
    () => suggestBeneficiariesFromFamily(spouse, familyChildren(contact, spouse)),
    [spouse, contact]
  );
  const contactFunds = useMemo(() => funds.filter((f) => f.contact_id === contactId), [funds, contactId]);

  // opening - the only client / the first
  useEffect(() => {
    if (!open) return;
    setContactId(contactIds.length === 1 ? contactIds[0] : contactIds[0] ?? null);
    setCreatedSimulationId(null);
    setError(null);
    setTitle("עדכון מוטבים");
    setStep("details");
    preparedForms.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contactIds]);

  // a client - their definition (or the family's suggestion), and the funds that do not match it
  useEffect(() => {
    if (!contactId) return;
    const saved = contact?.data?.beneficiaries ?? [];
    const initial = saved.length > 0 ? saved : familySuggestion;
    setDefinition(initial);
    setSaveAsDefinition(saved.length === 0);
    setSelectedKeys(new Set(contactFunds.filter((f) => !matchesDefinition(f.beneficiaries, initial)).map((f) => f.key)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const problem = definitionProblem(definition);
  const savedDefinition = contact?.data?.beneficiaries ?? [];
  const definitionChanged = JSON.stringify(definition) !== JSON.stringify(savedDefinition);

  const toggleFund = (key: string) =>
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const selectedFunds = contactFunds.filter((f) => selectedKeys.has(f.key));

  // each selected fund's form, pre-filled from the client, the fund and the beneficiaries
  const formItems: PreparedFormItem[] = useMemo(
    () => (contact?.id ? selectedFunds.map((f) => ({
      key: f.key,
      label: `${formatProductTypeUniversal(f.adjusted_product_type ?? f.product_type)} · ${formatHandlerCode(f.handler_code)} · ${f.policy_number}`,
      draft: () => taskFormService.draft({
        contact_id: contact.id as number,
        change_types: [SimulationChangeType.BENEFICIARY_UPDATE],
        handler_code: f.handler_code,
        product_type: f.product_type ?? null,
        adjusted_product_type: f.adjusted_product_type ?? null,
        policy_number: f.policy_number,
        beneficiaries: definition as unknown as Record<string, unknown>[],
      }),
    })) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contact?.id, selectedKeys, contactFunds, definition]
  );

  /** to the forms step */
  const goToForms = () => {
    if (!contact?.id) return;
    setStep("forms");
    preparedForms.load(formItems);
  };

  /** back to the beneficiaries: the drafts are made again (they were pre-filled from the beneficiaries) */
  const backToDetails = () => {
    setStep("details");
    preparedForms.reset();
  };

  const preparedNow = selectedFunds.filter((f) => preparedForms.preparedKeys.includes(f.key));

  const handleSubmit = async () => {
    if (!contact?.id || problem || selectedKeys.size === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      if (saveAsDefinition && definitionChanged) {
        await updateContact.mutateAsync({ id: contact.id, fields: { data: { ...contact.data, beneficiaries: definition } } });
      }
      const note = `עדכון מוטבים: ${definition.map((b) => `${beneficiaryName(b)} ${b.beneficiary_percentage}%`).join(", ")}`;
      const items: SimulationMimshakData[] = contactFunds
        .filter((f) => selectedKeys.has(f.key))
        .map((f) => ({
          ...withBeneficiaries(f.row, f.policy_number, definition),
          id: 0, // not yet created - ignored by the backend on create
          change_types: [SimulationChangeType.BENEFICIARY_UPDATE],
          note,
        }));
      const response = await simulationService.quickCreate({
        simulation: {
          contact_id: contact.id,
          reported_period: contactFunds[0].row.reported_period,
          name: title.trim() || "עדכון מוטבים",
          is_pre_advice: false,
        },
        items,
        create_tasks: true,
        source: "quickTaskAction",
      });
      // the forms prepared now - saved on their tasks and sent to the client for signature (the link that is sent
      // next lets the client sign them). A fund whose form could not be saved is prepared later, from the task.
      const failedKeys = await preparedForms.saveOnTasks((key) => {
        const f = contactFunds.find((fund) => fund.key === key);
        return (response.created_tasks ?? []).find(
          (t) => f && t.policy_number === f.policy_number && t.handler_code === f.handler_code
        )?.id;
      });
      const failed = contactFunds.filter((f) => failedKeys.includes(f.key)).map((f) => f.policy_number);
      showSnackbar(
        failed.length
          ? `נוצרו ${response.tasks_created} משימות. הטופס לא נשמר בקופות ${failed.join(", ")} - אפשר להכין אותו מעזרת הכנת הטפסים במשימה.`
          : `נוצרו ${response.tasks_created} משימות עדכון מוטבים${preparedNow.length ? ` · ${preparedNow.length} טפסים נשלחו לחתימה` : ""}`,
        failed.length ? SnackbarType.WARNING : SnackbarType.SUCCESS
      );
      if (response.simulation?.id != null) setCreatedSimulationId(response.simulation.id);
      else onClose();
    } catch (e) {
      console.error("Failed to create beneficiary update tasks:", e);
      setError("שגיאה ביצירת המשימות. נסה שנית.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth dir="rtl">
      <IconButton onClick={handleClose} disabled={submitting} size="small" sx={{ position: "absolute", left: 12, top: 12 }}>
        <CloseIcon fontSize="small" />
      </IconButton>
      <DialogTitle sx={{ pl: 6 }}>
        עדכון מוטבים
        {contact && (
          <Typography variant="body2" color="text.secondary">
            {formatName(contact)}
          </Typography>
        )}
      </DialogTitle>

      {createdSimulationId != null ? (
        <SimulationLinkSendStep
          simulationId={createdSimulationId}
          contact={contact}
          title={title.trim() || "עדכון מוטבים"}
          actionsCount={selectedKeys.size}
          onClose={onClose}
        />
      ) : step === "forms" ? (
        <>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <PreparedFormsStep items={formItems} forms={preparedForms} disabled={submitting} />

              {error && <Alert severity="error">{error}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={backToDetails} disabled={submitting} variant="outlined" size="large">
              חזור
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !preparedForms.ready}
              variant="contained"
              size="large"
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {preparedNow.length > 0 ? `צור משימות ושלח ${preparedNow.length} טפסים לחתימה` : `צור משימות (${selectedKeys.size})`}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {contactIds.length > 1 && (
                <ToggleButtonGroup size="small" exclusive value={contactId} onChange={(_e, v) => v && setContactId(v)}>
                  {contactIds.map((id) => {
                    const c = allContacts.find((x) => x.id === id);
                    return (
                      <ToggleButton key={id} value={id}>
                        {c ? formatName(c) : id}
                      </ToggleButton>
                    );
                  })}
                </ToggleButtonGroup>
              )}

              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  המוטבים
                </Typography>
                <BeneficiariesDefinitionEditor
                  value={definition}
                  onChange={setDefinition}
                  familySuggestion={familySuggestion}
                  defaultLastName={contact?.last_name ?? ""}
                  disabled={submitting}
                />
                {definitionChanged && (
                  <FormControlLabel
                    control={<Checkbox checked={saveAsDefinition} onChange={(e) => setSaveAsDefinition(e.target.checked)} />}
                    label="שמור גם כהגדרת המוטבים של הלקוח"
                  />
                )}
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  על אילו קופות להחיל
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  מסומנות מראש הקופות שהמוטבים בהן לא תואמים להגדרה
                </Typography>
                <List dense>
                  {contactFunds.map((f) => {
                    const matches = !problem && matchesDefinition(f.beneficiaries, definition);
                    return (
                      <ListItemButton key={f.key} onClick={() => toggleFund(f.key)} disabled={submitting}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox edge="start" size="small" checked={selectedKeys.has(f.key)} tabIndex={-1} disableRipple />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${formatProductTypeUniversal(f.adjusted_product_type ?? f.product_type)} · ${formatHandlerCode(f.handler_code)}`}
                          secondary={`${f.policy_number} · היום: ${
                            f.beneficiaries.length > 0
                              ? f.beneficiaries.map((b) => `${beneficiaryName(b)}${b.beneficiary_percentage != null ? ` ${b.beneficiary_percentage}%` : ""}`).join(", ")
                              : "ללא מוטבים"
                          }`}
                          slotProps={rightAlignedText}
                        />
                        <Chip size="small" label={matches ? "תואם" : "לא תואם"} color={matches ? "success" : "warning"} variant="outlined" />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>

              <TextField
                label="כותרת (תוצג ללקוח בלינק האישי)"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                disabled={submitting}
              />

              {error && <Alert severity="error">{error}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={handleClose} disabled={submitting} variant="outlined" size="large">
              חזור
            </Button>
            <Button
              onClick={goToForms}
              disabled={!!problem || selectedKeys.size === 0 || submitting || !contact}
              variant="contained"
              size="large"
            >
              המשך להכנת הטפסים ({selectedKeys.size})
            </Button>
          </DialogActions>
        </>
      )}

    </Dialog>
  );
};

export default BeneficiaryUpdateDialog;
